import React, { useState } from 'react';
import { Wand2, Loader } from 'lucide-react';
import theme from '../theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AIGenerator = ({ onGenerate }) => {
  const [prompt, setPrompt] = useState('');
  const [count, setCount] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [unavailable, setUnavailable] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setErrorMsg('Please enter a prompt.');
      return;
    }

    setGenerating(true);
    setErrorMsg('');
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${BACKEND_URL}/api/nft/generate-ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ prompt, count }),
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedImages(data.images);
        if (onGenerate) {
          onGenerate(data.images);
        }
      } else {
        // BUG #26 — mark AI Generator unavailable on any auth/config error and
        // switch the panel to a clean "coming soon" state instead of showing a
        // raw provider error alert to end users.
        let msg = 'AI generation is temporarily unavailable.';
        try {
          const err = await response.json();
          if ([401, 402, 403, 500, 501, 502, 503].includes(response.status)) {
            setUnavailable(true);
          } else if (err?.detail && typeof err.detail === 'string') {
            msg = err.detail;
          }
        } catch (_) { /* ignore body parse */ }
        setErrorMsg(msg);
      }
    } catch (error) {
      // Network / config missing — surface a clean message, not a raw alert.
      setErrorMsg('AI generation is temporarily unavailable. Please try Upload Files or From Gallery instead.');
    } finally {
      setGenerating(false);
    }
  };

  // Render a graceful "coming soon" panel when provider is unauthenticated
  // or misconfigured. Prevents the broken 401 alert reported in BUG #26.
  if (unavailable) {
    return (
      <div data-testid="ai-generator-unavailable" style={{
        background: theme.colors.bg.secondary,
        padding: '24px',
        borderRadius: theme.radius.md,
        textAlign: 'center'
      }}>
        <Wand2 size={22} style={{ color: theme.colors.primary, marginBottom: 8 }} />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 6 }}>
          AI Artwork Generator — Coming Soon
        </h3>
        <p style={{ fontSize: 13, color: theme.colors.text.secondary, lineHeight: 1.5 }}>
          AI artwork generation is not enabled on this account yet. You can still
          use <strong>Upload Files</strong> or <strong>From Gallery</strong> to
          add artwork to this Kickback.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          background: theme.colors.bg.secondary,
          padding: '24px',
          borderRadius: theme.radius.md,
          border: `1px solid ${theme.colors.borders}`,
        }}
      >
        <h3
          style={{
            fontSize: '16px',
            fontWeight: 600,
            color: theme.colors.text.primary,
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Wand2 size={20} style={{ color: theme.colors.primary }} />
          AI Artwork Generator
        </h3>

        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              color: theme.colors.text.primary,
              marginBottom: '8px',
            }}
          >
            Prompt
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe the artwork you want to generate"
            rows={3}
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${theme.colors.borders}`,
              borderRadius: theme.radius.sm,
              fontSize: '14px',
              color: theme.colors.text.primary,
              background: '#FFFFFF',
              resize: 'vertical',
            }}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 600,
              color: theme.colors.text.primary,
              marginBottom: '8px',
            }}
          >
            Number of Images (1-10 per generation)
          </label>
          <input
            type="number"
            min={1}
            max={10}
            value={count}
            onChange={(e) => setCount(parseInt(e.target.value) || 1)}
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${theme.colors.borders}`,
              borderRadius: theme.radius.sm,
              fontSize: '14px',
              color: theme.colors.text.primary,
              background: '#FFFFFF',
            }}
          />
        </div>

        <button
          onClick={handleGenerate}
          disabled={generating || !prompt.trim()}
          style={{
            width: '100%',
            padding: '12px',
            background:
              generating || !prompt.trim()
                ? theme.colors.bg.tertiary
                : theme.colors.primary,
            color: '#FFFFFF',
            border: 'none',
            borderRadius: theme.radius.sm,
            fontSize: '14px',
            fontWeight: 600,
            cursor: generating || !prompt.trim() ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          {generating ? (
            <>
              <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
              Generating...
            </>
          ) : (
            <>
              <Wand2 size={16} />
              Generate Artwork
            </>
          )}
        </button>
        {errorMsg && (
          <p data-testid="ai-generator-error" style={{
            marginTop: 10, fontSize: 12, color: '#991b1b',
            background: '#fef2f2', border: '1px solid #fecaca',
            padding: 8, borderRadius: 8,
          }}>{errorMsg}</p>
        )}
      </div>

      {generatedImages.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <h4
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: theme.colors.text.primary,
              marginBottom: '12px',
            }}
          >
            Generated NFTs ({generatedImages.length})
          </h4>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '16px',
            }}
          >
            {generatedImages.map((img) => (
              <div
                key={img.file_id}
                style={{
                  border: `1px solid ${theme.colors.borders}`,
                  borderRadius: theme.radius.sm,
                  overflow: 'hidden',
                  background: theme.colors.bg.card,
                }}
              >
                <img
                  src={`${BACKEND_URL}${img.url}`}
                  alt={img.filename}
                  style={{
                    width: '100%',
                    aspectRatio: '1',
                    objectFit: 'cover',
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIGenerator;
